import { RedeemedOffer, Offer } from 'api/types'
import { loyaltyApi } from 'api'
import { Discount, order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import { useCallback } from 'react'
import { err, ok, Result } from 'utils/result'
import { getErrorMessage } from 'utils/api'
import {
  AppliedDiscount,
  convertRedeemedOfferToDiscount,
  getOrderDiscount,
} from 'utils/loyalty'

export function useLoyalty() {
  const [redeemOffer] = loyaltyApi().useRedeemOfferMutation()
  const [unredeemOffer] = loyaltyApi().useRemoveOfferMutation()

  const applyOffer = useCallback(
    async (
      offer: Offer,
      userIdentifier: string
    ): Promise<Result<Discount, Error>> => {
      let discountId: string | null = null
      let redeemedOffer: RedeemedOffer | null = null

      try {
        const order = await squareOrderApi.retrieve()
        const result = await redeemOffer({
          userIdentifier,
          order,
          offerIdentifier: offer.id,
        })

        if (result.error) {
          return err(
            new Error(getErrorMessage(result.error, 'Error redeeming offer.'))
          )
        }

        redeemedOffer = result.data.offer

        const discount = convertRedeemedOfferToDiscount(redeemedOffer)

        // TODO: iOS isn't returning discount ID in `await squareOrderApi.addDiscount`
        // so fetching discount ID from the order. Use the discount ID returned from
        // `squareOrderApi.addDiscount` when it's fixed.
        await squareOrderApi.addDiscount(discount)
        const addedDiscount = await getOrderDiscount()
        discountId = addedDiscount?.uid as string

        return ok({
          ...discount,
          uid: discountId,
        })
      } catch (error) {
        if (redeemedOffer) {
          await unredeemOffer({
            userIdentifier,
            redemptionIdentifier: redeemedOffer.redemption_id,
          })
        }

        if (discountId) {
          squareOrderApi.removeDiscount(discountId)
        }

        return err(new Error('Error redeeming offer.'))
      }
    },
    [redeemOffer, unredeemOffer]
  )

  const removeOffer = useCallback(
    async (
      appliedDiscount: AppliedDiscount,
      userIdentifier: string
    ): Promise<Result<true, Error>> => {
      try {
        const result = await unredeemOffer({
          redemptionIdentifier: appliedDiscount.redemptionId,
          userIdentifier,
        })

        if (result.error) {
          return err(new Error('Error cancelling redemption'))
        }

        await squareOrderApi.removeDiscount(appliedDiscount.discountId)

        return ok(true)
      } catch (error) {
        return err(new Error('Error cancelling redemption'))
      }
    },
    [unredeemOffer]
  )

  return {
    applyOffer,
    removeOffer,
  }
}

export type ApplyOfferResult = ReturnType<
  ReturnType<typeof useLoyalty>['applyOffer']
>

export type RemoveOfferResult = ReturnType<
  ReturnType<typeof useLoyalty>['removeOffer']
>
